import React, { Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dropdown } from '@getro/rombo';
import { Box } from 'rebass/styled-components';

import { CreditCard, ExternalLink, Settings, Users } from 'lucide-react';
import { addProtocolToUrl } from 'helpers/urlHelpers';
import { toggleMenu } from '../../../features/mainMenu';
import { Card } from '../../atoms/Card';
import { networksSelector } from '../../../redux/selectors';
import ActiveNetwork from './activeNetwork';

export const NetworkOption = () => {
  const { networks, active } = useSelector(networksSelector);
  const dispatch = useDispatch();
  const { url: jobBoardUrl, logoUrl: logo, isManager, ...rest } = networks[active];
  const current = { logo, ...rest };

  const networkLinks = useMemo(() => {
    const links = [];

    if (isManager) {
      links.push({
        label: 'Settings',
        to: `/networks/${rest.slug}/settings/general`,
        icon: Settings,
        target: '_self',
      });
    }

    if (!rest.isOnlyAlly) {
      links.push({
        label: 'Team members',
        to: `/networks/${rest.slug}/settings/team-members`,
        icon: Users,
        target: '_self',
      });
    }

    if (!rest.isOnlyCompany) {
      links.push({
        label: 'Plans and billing',
        to: `/networks/${rest.slug}/settings/plans-and-billing`,
        icon: CreditCard,
        target: '_self',
      });
    }

    if (jobBoardUrl && rest?.features?.includes('job_board')) {
      links.push({
        label: 'Open Job Board',
        onClick: () => window.open(addProtocolToUrl(jobBoardUrl), '_blank'),
        icon: ExternalLink,
        target: '_blank',
        to: '',
      });
    }

    return links ?? [];
  }, [isManager, jobBoardUrl, rest]);

  return (
    <Box
      sx={{
        pointerEvents: networkLinks.length > 0 ? 'auto' : 'none',
      }}
    >
      <Dropdown
        data-cy="network-selector-multiple"
        placement="bottom-start"
        triggerProps={{
          variant: 'styleless',
          children: <ActiveNetwork current={current} canSelectNetwork={networkLinks.length > 0} />,
        }}
        dropDownProps={{
          sx: {
            maxHeight: 'calc(100% - 32px)',
            overflowY: 'auto',
            boxShadow: 'rgb(0 0 0 / 10%) 0px 1px 8px, rgb(0 0 0 / 15%) 0px 8px 24px',
            borderRadius: '6xp',
            padding: 0,
          },
        }}
      >
        <Card p="0" py="8px" width="240px">
          <Box
            px="16px"
            sx={{
              a: {
                color: 'text.main',
                textDecoration: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                lineHeight: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                py: '4px',
                ':hover': {
                  color: 'text.dark',
                },
              },
            }}
          >
            {networkLinks.map(({ label, icon, target = '_blank', ...props }, index) => (
              <Fragment key={label}>
                {index === 3 && isManager && <Box my="8px" bg="border.subtle" width="100%" height="1px" />}
                <Link
                  target={target}
                  rel="noopener noreferrer"
                  color="text.main"
                  sx={{}}
                  onClick={() => {
                    const width =
                      window.screen.availWidth < window.innerWidth ? window.screen.availWidth : window.innerWidth;

                    if (width < 990) {
                      dispatch(toggleMenu());
                    }
                  }}
                  {...props}
                >
                  <Box as={icon} size="16px" />
                  <Box as="span"> {label}</Box>
                </Link>
              </Fragment>
            ))}
          </Box>
        </Card>
      </Dropdown>
    </Box>
  );
};
